export interface OwnedMutation {
  id: string;
  ownerId?: string;
}

const OWNER_PREFIX = 'proyecto-base:mutation-owner:';
const REPLAY_LOCK = 'proyecto-base:mutation-replay';

/** A live tab owns its pending writes; other tabs can recover them after it closes. */
export class TrackedMutationCoordinator {
  readonly ready: Promise<void>;

  constructor(
    private readonly locks: LockManager | undefined,
    readonly ownerId: string,
  ) {
    if (!locks) {
      this.ready = Promise.resolve();
      return;
    }
    // The browser releases this presence lock when this document closes.
    this.ready = new Promise<void>((resolve, reject) => {
      void locks
        .request(`${OWNER_PREFIX}${ownerId}`, () => {
          resolve();
          return new Promise<void>(() => {});
        })
        .catch(reject);
    });
  }

  async replay<T extends OwnedMutation, R>(
    getPending: () => T[],
    isExecuting: (item: T) => boolean,
    send: (item: T) => Promise<R>,
  ): Promise<PromiseSettledResult<R>[]> {
    await this.ready;
    const drain = async () => {
      const snapshot = await this.locks?.query();
      const liveOwners = new Set(snapshot?.held?.map((lock) => lock.name));
      // Read again inside the replay lock: another tab may have already drained it.
      const pending = getPending().filter((item) => {
        if (isExecuting(item)) return false;
        if (item.ownerId === this.ownerId) return true;
        if (!this.locks) return false;
        return (
          !item.ownerId || !liveOwners.has(`${OWNER_PREFIX}${item.ownerId}`)
        );
      });
      return Promise.allSettled(pending.map((item) => send(item)));
    };
    // Serialize orphan recovery across tabs without blocking the owner's live writes.
    return this.locks ? this.locks.request(REPLAY_LOCK, drain) : drain();
  }
}
